import { readSessionActiveOrgId } from '@/lib/utils/activeOrganisation';
import { getAppFromHostname, getSportsFiHostContext } from '@/lib/auth/appAccess';
import { getAuthCleanupPathWithLoginReturnUrl } from '@/lib/auth/clientRedirects';
import {
  isAuthenticationGraphQLError,
  isAuthenticationNetworkError,
  isIdentityProviderUnavailableGraphQLError,
  isIdentityProviderUnavailableNetworkError,
  type NetworkErrorWithResult,
} from '@/lib/auth/sessionErrors';
import { ApolloClient, InMemoryCache, Observable, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { fromPromise } from '@apollo/client/link/utils';
import { getSession } from 'next-auth/react';

type CreateApolloClientOptions = {
  accessToken?: string;
  organisationId?: string;
};

let browserAccessTokenOverride: string | null = null;
let authRecoveryInFlight: Promise<boolean> | null = null;

export const setBrowserApolloAccessTokenOverride = (accessToken: string | null | undefined): void => {
  browserAccessTokenOverride = accessToken != null && accessToken.length > 0 ? accessToken : null;
};

export const getBrowserApolloAccessToken = (fallbackAccessToken: string): string => {
  if (typeof window === 'undefined') {
    return fallbackAccessToken;
  }

  return browserAccessTokenOverride ?? fallbackAccessToken;
};

export { isAuthenticationGraphQLError } from '@/lib/auth/sessionErrors';

const browserFetchFailureMessages = [
  'Failed to fetch',
  'NetworkError when attempting to fetch resource.',
  'NetworkError when attempting to fetch resource',
];

const browserFetchCancellationMessages = [
  'AbortError',
  'Load failed',
  'The operation was aborted',
  'aborted',
  'canceled',
  'cancelled',
];

export const isRecoverableBrowserFetchNetworkError = (networkError: unknown): boolean => {
  const message =
    networkError instanceof Error
      ? networkError.message
      : typeof (networkError as { message?: unknown } | null)?.message === 'string'
        ? String((networkError as { message: string }).message)
        : '';

  if (browserFetchCancellationMessages.some((browserMessage) => message.includes(browserMessage))) {
    return false;
  }

  return browserFetchFailureMessages.some((browserMessage) => message.includes(browserMessage));
};

const redirectToReauth = (): void => {
  window.location.replace(getAuthCleanupPathWithLoginReturnUrl());
};

const completeWithoutRetry = () =>
  new Observable<never>((observer) => {
    observer.complete();
  });

export const recoverBrowserAuthSession = async (): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (authRecoveryInFlight != null) {
    return authRecoveryInFlight;
  }

  authRecoveryInFlight = (async () => {
    try {
      const session = await getSession();
      const refreshedAccessToken = session?.error === 'RefreshAccessTokenError' ? null : session?.accessToken;

      if (refreshedAccessToken != null && refreshedAccessToken.length > 0) {
        setBrowserApolloAccessTokenOverride(refreshedAccessToken);

        return true;
      }

      redirectToReauth();

      return false;
    } catch {
      redirectToReauth();

      return false;
    } finally {
      authRecoveryInFlight = null;
    }
  })();

  return authRecoveryInFlight;
};

const organisationContextFreeOperations = new Set([
  'OrganisationsGetByCurrentUser',
  'SensitiveActionConfirmationCreate',
  'SensitiveActionPasskeyChallengeCreate',
  'SensitiveActionPasskeyConfirmationVerify',
  'SensitiveActionPolicyGet',
  'SensitiveActionPolicyInventoryGet',
]);

export const shouldAttachOrganisationHeader = ({
  hostname,
  operationName,
}: {
  hostname: string | null;
  operationName?: string;
}): boolean => {
  if (operationName != null && organisationContextFreeOperations.has(operationName)) {
    return false;
  }

  if (hostname == null) {
    return true;
  }

  const currentApp = getAppFromHostname(hostname);

  if (currentApp === 'admin') {
    return false;
  }

  const sportsFiHostContext = getSportsFiHostContext(hostname);
  const isNeutralSportsFiHost = sportsFiHostContext != null && currentApp == null;

  return !isNeutralSportsFiHost;
};

export const createApolloClient = ({ accessToken = '', organisationId = '' }: CreateApolloClientOptions) => {
  const httpLink = createHttpLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_URL || 'https://api.local.sportsfi.tech/graphql',
  });

  const authLink = setContext((operation, { headers }) => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : null;
    const currentOrganisationId = shouldAttachOrganisationHeader({
      hostname,
      operationName: operation.operationName,
    })
      ? typeof window !== 'undefined'
        ? (readSessionActiveOrgId() ?? organisationId)
        : organisationId
      : '';

    return {
      headers: {
        ...headers,
        authorization: getBrowserApolloAccessToken(accessToken)
          ? `Bearer ${getBrowserApolloAccessToken(accessToken)}`
          : '',
        ...(currentOrganisationId ? { 'x-organisation-id': currentOrganisationId } : {}),
      },
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    const hasAuthenticationGraphQLError = graphQLErrors?.some(isAuthenticationGraphQLError) ?? false;
    const hasAuthenticationNetworkError = isAuthenticationNetworkError(networkError);
    const hasIdentityProviderUnavailableGraphQLError =
      graphQLErrors?.some(isIdentityProviderUnavailableGraphQLError) ?? false;
    const hasIdentityProviderUnavailableNetworkError = isIdentityProviderUnavailableNetworkError(networkError);
    const hasPotentiallyStaleBrowserSessionNetworkError =
      typeof window !== 'undefined' &&
      window.navigator.onLine !== false &&
      isRecoverableBrowserFetchNetworkError(networkError) &&
      getBrowserApolloAccessToken(accessToken).length > 0;

    if (
      (hasAuthenticationGraphQLError ||
        hasAuthenticationNetworkError ||
        hasIdentityProviderUnavailableGraphQLError ||
        hasIdentityProviderUnavailableNetworkError ||
        hasPotentiallyStaleBrowserSessionNetworkError) &&
      typeof window !== 'undefined'
    ) {
      console.warn('GraphQL authentication failure', {
        operationName: operation.operationName,
        graphQLErrorCount: graphQLErrors?.length ?? 0,
        identityProviderUnavailable:
          hasIdentityProviderUnavailableGraphQLError || hasIdentityProviderUnavailableNetworkError,
        recoverableNetworkFailure: hasPotentiallyStaleBrowserSessionNetworkError,
        statusCode: (networkError as NetworkErrorWithResult | null)?.statusCode,
      });

      if (operation.getContext().authRecoveryRetry === true) {
        redirectToReauth();

        return completeWithoutRetry();
      }

      operation.setContext({ authRecoveryRetry: true });

      return fromPromise(recoverBrowserAuthSession()).flatMap((didRecover) =>
        didRecover ? forward(operation) : completeWithoutRetry()
      );
    }

    if (graphQLErrors != null && graphQLErrors.length > 0) {
      console.warn('GraphQL errors', {
        operationName: operation.operationName,
        errors: graphQLErrors.map((error) => ({
          message: error.message,
          code: error.extensions?.code,
          path: error.path,
        })),
      });
    }

    if (networkError != null) {
      console.warn('GraphQL network error', {
        operationName: operation.operationName,
        name: networkError.name,
        message: networkError.message,
        statusCode: (networkError as NetworkErrorWithResult | null)?.statusCode,
      });
    }
  });

  return new ApolloClient({
    link: errorLink.concat(authLink).concat(httpLink),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};
